import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import TitleIcon from '@mui/icons-material/Title';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

interface ToolbarButtonProps {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolbarButton({ title, active, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          color={active ? 'primary' : 'default'}
          sx={{
            borderRadius: 1,
            bgcolor: active ? 'action.selected' : 'transparent',
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB per image keeps total email size sane

function Toolbar({ editor, onPickImage }: { editor: Editor; onPickImage: () => void }) {
  const setLink = useCallback(() => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <Stack
      direction="row"
      spacing={0.25}
      sx={{
        p: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
        flexWrap: 'wrap',
      }}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <Stack direction="row" spacing={0.25}>
        <ToolbarButton
          title="Bold (⌘B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic (⌘I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleMark?.('underline').run()}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <StrikethroughSIcon fontSize="small" />
        </ToolbarButton>
      </Stack>
      <Stack direction="row" spacing={0.25}>
        <ToolbarButton
          title="Heading 1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <LooksOneIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Heading 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <LooksTwoIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Heading 3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <TitleIcon fontSize="small" />
        </ToolbarButton>
      </Stack>
      <Stack direction="row" spacing={0.25}>
        <ToolbarButton
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumberedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Blockquote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <FormatQuoteIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Code block"
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <CodeIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Horizontal rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRuleIcon fontSize="small" />
        </ToolbarButton>
      </Stack>
      <Stack direction="row" spacing={0.25}>
        <ToolbarButton
          title="Link"
          active={editor.isActive('link')}
          onClick={setLink}
        >
          <LinkIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Remove link"
          disabled={!editor.isActive('link')}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <LinkOffIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton title="Insert image (max 2MB)" onClick={onPickImage}>
          <ImageIcon fontSize="small" />
        </ToolbarButton>
      </Stack>
      <Stack direction="row" spacing={0.25}>
        <ToolbarButton
          title="Undo (⌘Z)"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <UndoIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo (⌘⇧Z)"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <RedoIcon fontSize="small" />
        </ToolbarButton>
      </Stack>
    </Stack>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 280,
}: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        inline: false,
        // No upload — we embed as data: URIs while editing. The backend
        // extracts them at send time and packs them as Content-ID
        // attachments in a multipart/related MIME message.
        HTMLAttributes: { style: 'max-width: 100%; height: auto;' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap returns "<p></p>" for an empty doc — collapse to '' so
      // the parent can detect "no body".
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Keep editor content in sync if the parent resets the value
  // (e.g., Clear button). Avoid the round-trip when the user is typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const normalized = value || '<p></p>';
    if (current !== normalized && !editor.isFocused) {
      editor.commands.setContent(normalized, { emitUpdate: false });
    }
  }, [value, editor]);

  const pickImage = useCallback(() => fileInputRef.current?.click(), []);

  const handleImageFile = useCallback(
    (file: File | undefined) => {
      if (!file || !editor) return;
      if (!file.type.startsWith('image/')) {
        window.alert('Please select an image file.');
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        window.alert(
          `Image is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 2MB.`,
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || '');
        if (!dataUrl.startsWith('data:image/')) return;
        editor.chain().focus().setImage({ src: dataUrl }).run();
      };
      reader.readAsDataURL(file);
    },
    [editor],
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '& .ProseMirror': {
          minHeight,
          maxHeight: 600,
          overflowY: 'auto',
          p: 2,
          outline: 'none',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          fontSize: 14,
          lineHeight: 1.6,
        },
        '& .ProseMirror p.is-editor-empty:first-of-type::before': {
          content: `"${placeholder || ''}"`,
          color: 'text.disabled',
          float: 'left',
          height: 0,
          pointerEvents: 'none',
        },
        '& .ProseMirror a': { color: 'primary.main', textDecoration: 'underline' },
        '& .ProseMirror blockquote': {
          borderLeft: 3,
          borderColor: 'divider',
          pl: 2,
          color: 'text.secondary',
          my: 1,
        },
        '& .ProseMirror pre': {
          bgcolor: 'grey.100',
          p: 1,
          borderRadius: 1,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 13,
        },
        '& .ProseMirror hr': { my: 2, border: 0, borderTop: 1, borderColor: 'divider' },
      }}
    >
      {editor && <Toolbar editor={editor} onPickImage={pickImage} />}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={e => {
          handleImageFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <EditorContent editor={editor} />
    </Box>
  );
}
